import { useEffect, useState, useCallback } from "react";
import {
  fetchChecklists,
  createChecklist,
  updateChecklist,
  deleteChecklist,
  archiveChecklist,
  fetchChecklistItems,
  createChecklistItem,
  updateChecklistItem,
  deleteChecklistItem,
} from "../services/checklistsService";

export function useChecklists({ workspaceId, token }) {
  const [checklists, setChecklists] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [expandedId, setExpandedId] = useState(null);
  const [newChecklistTitle, setNewChecklistTitle] = useState("");
  const [newItemText, setNewItemText] = useState("");

  // ---------- GET ----------
  const loadChecklists = useCallback(async () => {
    if (!workspaceId) return;
    setLoading(true);
    setError("");

    try {
      const data = await fetchChecklists({ workspaceId, token });
      setChecklists(data);
    } catch (err) {
      console.error(err);
      setError("Failed to load checklists.");
    } finally {
      setLoading(false);
    }
  }, [workspaceId, token]);

  useEffect(() => {
    loadChecklists();
  }, [loadChecklists]);

  const loadItems = async (checklistId) => {
    try {
      const items = await fetchChecklistItems({ token, checklistId });
      setChecklists((prev) =>
        prev.map((c) => (c.id === checklistId ? { ...c, items } : c))
      );
    } catch (err) {
      console.error(err);
      setError("Failed to load checklist items.");
    }
  };

  // ---------- CREATE ----------
  const handleCreateChecklist = async (title = null) => {
    if (!title) title = newChecklistTitle;
    if (!title.trim()) return;
    try {
      const newChecklist = await createChecklist({
        token,
        checklist: { workspaceId: workspaceId, title: title },
      });

      setChecklists((prev) => [...prev, { ...newChecklist, items: [] }]);
      setNewChecklistTitle("");

      return { ...newChecklist, items: [] }; // ✅ return new checklist

    } catch (err) {
      console.error(err);
      setError("Failed to create checklist.");
      return null
    }
  };

  // ---------- ARCHIVE & RATE ----------
  const handleArchiveChecklist = async (id, rating, comment) => {
    try {
      const updated = await archiveChecklist({
        token,
        id,
        rating,
        comment,
      });

      // Update local state: Replace the checklist with the archived version
      setChecklists((prev) =>
        prev.map((c) => (c.id === id ? { ...c, ...updated } : c))
      );
      
      return updated;
    } catch (err) {
      console.error(err);
      setError("Failed to archive checklist.");
      throw err; // Throw so the UI (modal) can handle showing the error
    }
  };

  const handleAddItem = async (checklistId, title=null, date=null) => {
    if (!title) title = newItemText;
    if (!title.trim()) return;
    try {
      // Prepare the item object
      const item = { description: title };

      // Only include date if it's provided and valid
      if (date) {
        const parsedDate = new Date(date);
        if (!isNaN(parsedDate.getTime())) {
          // Convert to ISO string (backend-friendly format)
          item.itemDate = parsedDate.toISOString().split('T')[0]; // 'YYYY-MM-DD'
        } else {
          console.warn('Invalid date format, ignoring date.');
        }
      }

      const newItem = await createChecklistItem({
        token,
        checklistId,
        item: item,

      });
      setChecklists((prev) =>
        prev.map((c) =>
          c.id === checklistId ? { ...c, items: [...(c.items || []), newItem] } : c
        )
      );
      setNewItemText("");
    } catch (err) {
      console.error(err);
      setError("Failed to add item.");
    }
  };

  // ---------- UPDATE ----------
  const handleRenameChecklist = async (id, title) => {
    try {
      const updated = await updateChecklist({ token, id, checklist: { title } });
      setChecklists((prev) =>
        prev.map((c) => (c.id === id ? { ...c, title: updated.title } : c))
      );
    } catch (err) {
      console.error(err);
      setError("Failed to update checklist.");
    }
  };

  const handleToggleItem = async (checklistId, itemId, isDone) => {
    try {
      const updated = await updateChecklistItem({
        token,
        checklistId,
        itemId,
        item: { isDone: !isDone },
      });
      setChecklists((prev) =>
        (prev || []).map((c) =>
          c.id === checklistId
            ? {
                ...c,
                items: (c.items || []).map((i) => (i.id === itemId ? updated : i)),
              }
            : c
        )
      );
    } catch (err) {
      console.error(err);
      setError("Failed to update item.");
    }
  };

  const handleUpdateItemDate = async (checklistId, itemId, itemDate) => {
    try {
      const dateString = itemDate.slice(0, 10); // in case a full ISO string is passed
      const updated = await updateChecklistItem({
        token,
        checklistId,
        itemId,
        item: { itemDate: dateString },
      });
      setChecklists((prev) =>
      prev.map((c) =>
        c.id === checklistId
          ? {
              ...c,
              items: c.items.map((i) =>
                i.id === itemId
                  ? { ...updated, itemDate: dateString } // force date string
                  : i
              ),
            }
          : c
      )
    );
    } catch (err) {
      console.error(err);
      setError("Failed to update item date.");
    }
  };

  const handleUpdateItemDescription = async (
    checklistId,
    itemId,
    description
  ) => {
    if (!description.trim()) return;

    try {
      const updated = await updateChecklistItem({
        token,
        checklistId,
        itemId,
        item: { description },
      });

      setChecklists((prev) =>
        prev.map((c) =>
          c.id === checklistId
            ? {
                ...c,
                items: c.items.map((i) =>
                  i.id === itemId ? updated : i
                ),
              }
            : c
        )
      );
    } catch (err) {
      console.error(err);
      setError("Failed to update item description.");
    }
  };


  // ---------- DELETE ----------
  const handleDeleteChecklist = async (id) => {
    try {
      await deleteChecklist({ token, id });
      setChecklists((prev) => prev.filter((c) => c.id !== id));
    } catch (err) {
      console.error(err);
      setError("Failed to delete checklist.");
    }
  };

  const handleRemoveItem = async (itemId) => {
    try {
      await deleteChecklistItem({ token, itemId });

      setChecklists((prev) =>
        prev.map((c) =>
          c.items
            ? { ...c, items: c.items.filter((i) => i.id !== itemId) }
            : c
        )
      );
    } catch (err) {
      console.error(err);
      setError("Failed to delete item.");
    }
  };

  return {
    checklists,
    setChecklists,
    loading,
    setLoading,
    error,
    setError,
    expandedId,
    setExpandedId,
    newChecklistTitle,
    setNewChecklistTitle,
    newItemText,
    setNewItemText,
    loadChecklists,
    loadItems,
    handleCreateChecklist,
    handleDeleteChecklist,
    handleRenameChecklist,
    handleArchiveChecklist,
    handleAddItem,
    handleToggleItem,
    handleRemoveItem,
    handleUpdateItemDate,
    handleUpdateItemDescription,
  };
}
